// Shared top navigation (plan §3.1). One bar mounted by every post-auth screen,
// replacing the divergent hand-rolled `.topbar`/`.who` blocks. Three links
// (Play / Players / Games) + a right cluster (ratings chip · mute · theme ·
// account). Keep the account entry a plain link (no JS dropdown) for v1.

import { api } from "../api.ts";
import { esc } from "../dom.ts";
import { currentUser } from "../session.ts";
import { muteButton } from "./mute.ts";
import { themeButton } from "../theme.ts";
import { brandLockup, BRAND_NAME } from "./brand.ts";
import { languageToggle } from "../i18n/lang.ts";
import { t } from "../i18n/index.ts";

export type NavKey = "play" | "players" | "games" | "study" | "activity" | "admin" | null;

// The ratings chip wants the same data on every screen; cache it briefly so
// navigating between screens doesn't refetch on each mount (plan §10 gap).
let ratingsCache: { at: number; text: string } | null = null;
const RATINGS_TTL_MS = 60_000;

/** Invalidate the cached ratings chip (e.g. after a rated game finishes). */
export function invalidateRatings(): void {
  ratingsCache = null;
}

/** Build the shared top nav. `active` highlights the current section. */
export function topNav(active: NavKey = null): HTMLElement {
  const user = currentUser();
  const uname = user?.username ?? "";
  const isAdmin = user?.role === "admin" || user?.role === "superuser";
  const el = document.createElement("header");
  el.className = "topnav";
  el.innerHTML = `
    <a class="brand" data-link href="/" aria-label="${BRAND_NAME}">${brandLockup}</a>
    <nav class="nav-links" aria-label="${t("nav.primary")}">
      <a data-link href="/" class="${active === "play" ? "active" : ""}">${t("nav.play")}</a>
      <a data-link href="/club" class="${active === "players" ? "active" : ""}">Club</a>
      <a data-link href="/my-games" class="${active === "games" ? "active" : ""}">My games</a>
      ${isAdmin ? `<a data-link href="/admin" class="${active === "admin" ? "active" : ""}">${t("nav.admin")}</a>` : ""}
    </nav>
    <div class="nav-right">
      <span class="my-ratings" hidden></span>
      <span class="mute-slot"></span>
      <span class="lang-slot"></span>
      <span class="theme-slot"></span>
      <a class="uname" data-link href="${user ? `/player/${encodeURIComponent(uname)}` : "/my-games"}">${user ? esc(uname) : "Save my games"}</a>
    </div>`;

  el.querySelector<HTMLElement>(".mute-slot")!.append(muteButton());
  el.querySelector<HTMLElement>(".lang-slot")!.append(languageToggle());
  el.querySelector<HTMLElement>(".theme-slot")!.append(themeButton());

  const chip = el.querySelector<HTMLElement>(".my-ratings")!;
  const paintChip = (text: string) => {
    if (!text) return;
    chip.textContent = text;
    chip.hidden = false;
  };

  if (user) {
    const fresh = ratingsCache && Date.now() - ratingsCache.at < RATINGS_TTL_MS;
    if (fresh) {
      paintChip(ratingsCache!.text);
    } else {
      void (async () => {
        try {
          const me = await api.player(user.username);
          const parts: string[] = [];
          if (me.ratings.blitz)
            parts.push(`⚡ ${me.ratings.blitz.rating}${me.ratings.blitz.provisional ? "?" : ""}`);
          if (me.ratings.rapid)
            parts.push(`⏱ ${me.ratings.rapid.rating}${me.ratings.rapid.provisional ? "?" : ""}`);
          const text = parts.join(" · ");
          ratingsCache = { at: Date.now(), text };
          // The nav may have been replaced by a navigation mid-fetch; guard.
          if (el.isConnected) paintChip(text);
        } catch {
          /* non-fatal: the chip stays hidden */
        }
      })();
    }
  }

  return el;
}
